import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useStreamSummary } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Badge } from 'src/components/ui/Badge';
import { Icon } from 'src/components/ui/Icon';
import { Skeleton } from 'src/components/ui/Skeleton';
import { FLOW_STAGES, PAID_FLOW_STAGES, flowStandings, type FlowStage, type FlowStageId, type FlowStanding } from 'src/lib/pipelineFlow';
import type { Tone } from 'src/lib/tone';

/**
 * The whole process on one screen, in language that does not assume the product.
 *
 * Four illustrated moments organize the ten stages. Every stage previews the result it creates;
 * opening one reveals a visual input → work → output path, plus cost and failure information.
 * The map collapses to a single column on narrow screens and keeps each explanation attached to
 * its own stage, so the sequence never loses its place.
 */

const STANDING_TONE: Record<FlowStanding, Tone> = {
  done: 'supported',
  partial: 'partial',
  skipped: 'unresolved',
  not_reached: 'unresolved',
  unknown: 'info',
};

const STANDING_LABEL: Record<FlowStanding, string> = {
  done: 'hecho',
  partial: 'parcial',
  skipped: 'no se ejecutó',
  not_reached: 'no llegó aquí',
  unknown: '',
};

/**
 * Where a stage's work actually happens.
 *
 * The operational fact this screen was missing. "costo externo" said that money leaves, not that
 * the work does — and which of the ten boxes run on the sleeping box is what decides how long a
 * broadcast takes and what happens when the box is off.
 */
function runsOn(id: FlowStageId): { label: string; tone: 'box' | 'external' } {
  return PAID_FLOW_STAGES.has(id)
    ? { label: 'Proveedor externo · de pago', tone: 'external' }
    : { label: 'Sistema Pressentia', tone: 'box' };
}

const FLOW_PHASES = [
  { id: 'escuchar', number: '01', title: 'Escuchar', summary: 'Del video al guion', stages: ['captura', 'transcript'] },
  { id: 'entender', number: '02', title: 'Entender', summary: 'Del guion a las historias', stages: ['topics', 'claims', 'prenota'] },
  { id: 'comprobar', number: '03', title: 'Comprobar', summary: 'Relevancia y fuentes', stages: ['x_review', 'verification'] },
  { id: 'publicar', number: '04', title: 'Escribir y aprobar', summary: 'De la historia a la nota', stages: ['enrichment', 'draft', 'review'] },
] as const satisfies ReadonlyArray<{
  id: string;
  number: string;
  title: string;
  summary: string;
  stages: readonly FlowStageId[];
}>;

const STAGE_ICON: Record<FlowStageId, 'broadcasts' | 'voices' | 'dashboard' | 'check' | 'trends' | 'search' | 'processing' | 'posts'> = {
  captura: 'broadcasts',
  transcript: 'voices',
  topics: 'dashboard',
  claims: 'check',
  prenota: 'posts',
  x_review: 'trends',
  verification: 'search',
  enrichment: 'processing',
  draft: 'posts',
  review: 'check',
};

const STAGE_OUTPUT: Record<FlowStageId, string> = {
  captura: 'Audio dividido en tramos',
  transcript: 'Guion con voces y tiempos',
  topics: 'Temas y afirmaciones detectados',
  claims: 'Afirmaciones claras con evidencia',
  prenota: 'Una historia por tema',
  x_review: 'Calificación de relevancia',
  verification: 'Veredictos y fuentes externas',
  enrichment: 'Plan de la nota',
  draft: 'Tres borradores con respaldo',
  review: 'Nota aprobada por una persona',
};

export function FlowWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const [openStage, setOpenStage] = useState<FlowStageId | null>(null);

  const toggle = (id: FlowStageId) => setOpenStage(current => (current === id ? null : id));

  return streamId ? <StreamFlow streamId={streamId} openStage={openStage} onToggle={toggle} /> : <GenericFlow openStage={openStage} onToggle={toggle} />;
}

type SpineProps = {
  openStage: FlowStageId | null;
  onToggle: (id: FlowStageId) => void;
  standings?: Record<FlowStageId, { standing: FlowStanding; detail: string }>;
  streamId?: string;
};

function GenericFlow({ openStage, onToggle }: Omit<SpineProps, 'standings' | 'streamId'>) {
  return (
    <div className="page-wide flow-workspace">
      <PageHeader title="Cómo funciona">
        Sigue el camino de una transmisión hasta una nota revisada. Cada tarjeta muestra qué produce y puedes abrirla
        para ver qué sucede en ese paso.
      </PageHeader>

      <FlowOverview />
      <FlowSpine openStage={openStage} onToggle={onToggle} />
    </div>
  );
}

function StreamFlow({ streamId, openStage, onToggle }: SpineProps & { streamId: string }) {
  const summary = useStreamSummary(streamId);

  if (summary.isPending) {
    return (
      <div className="page-wide flow-workspace">
        <Skeleton variant="rows" count={6} />
      </div>
    );
  }

  if (summary.isError) {
    return (
      <div className="page-wide flow-workspace">
        <LoadFailure title="No se pudo cargar el estado de la transmisión" error={summary.error} onRetry={summary.refetch} />
      </div>
    );
  }

  return (
    <div className="page-wide flow-workspace">
      <PageHeader title="Cómo funciona esta transmisión">
        Sigue los productos que dejó cada etapa. Abre un paso para entender cómo se creó y qué pasa si algo falla.
      </PageHeader>
      <FlowOverview />
      <FlowSpine openStage={openStage} onToggle={onToggle} standings={flowStandings(summary.data.summary)} streamId={streamId} />
    </div>
  );
}

function FlowOverview() {
  return (
    <section className="flow-overview" aria-label="Resumen visual del proceso">
      <div className="flow-overview__intro">
        <span className="flow-overview__kicker">EL RECORRIDO EN UN VISTAZO</span>
        <h2>Del video a una nota revisada</h2>
      </div>
      <div className="flow-overview__route">
        <div className="flow-overview__endpoint">
          <span className="flow-overview__symbol" aria-hidden="true"><Icon name="broadcasts" /></span>
          <span><small>ENTRA</small><strong>Una transmisión</strong><em>en vivo o grabada</em></span>
        </div>
        <span className="flow-overview__connector" aria-hidden="true"><span>10 pasos</span></span>
        <div className="flow-overview__endpoint flow-overview__endpoint--finish">
          <span className="flow-overview__symbol" aria-hidden="true"><Icon name="check" /></span>
          <span><small>SALE</small><strong>Una nota aprobada</strong><em>con fuentes y revisión humana</em></span>
        </div>
      </div>
      <p className="flow-overview__rule"><span aria-hidden="true">✓</span> Ninguna nota se publica sin aprobación de una persona.</p>
    </section>
  );
}

function FlowSpine({ openStage, onToggle, standings, streamId }: SpineProps) {
  return (
    <ol className="flow-map" aria-label="Etapas del proceso, agrupadas en cuatro momentos">
      {FLOW_PHASES.map(phase => (
        <li key={phase.id} className="flow-phase" data-phase={phase.id}>
          <header className="flow-phase__head">
            <span className="flow-phase__number">{phase.number}</span>
            <div>
              <h3>{phase.title}</h3>
              <p>{phase.summary}</p>
            </div>
            <span className="flow-phase__count">{phase.stages.length} pasos</span>
          </header>
          <ol className="flow-phase__stages">
            {phase.stages.map(stageId => {
              const stage = FLOW_STAGES.find(candidate => candidate.id === stageId);
              if (!stage) return null;
              const index = FLOW_STAGES.indexOf(stage);
              const standing = standings?.[stage.id];
              const open = stage.id === openStage;
              const where = runsOn(stage.id);
              const detailId = `flow-detail-${stage.id}`;

              return (
                <li key={stage.id} className="flow-stage" data-open={open} data-standing={standing?.standing ?? 'unknown'}>
                  <button
                    type="button"
                    className="flow-stage__trigger"
                    aria-expanded={open}
                    aria-controls={open ? detailId : undefined}
                    onClick={() => onToggle(stage.id)}
                  >
                    <span className="flow-stage__identity">
                      <span className="flow-stage__icon" aria-hidden="true"><Icon name={STAGE_ICON[stage.id]} /></span>
                      <span className="flow-stage__title"><small>PASO {String(index + 1).padStart(2, '0')}</small><strong>{stage.label}</strong></span>
                      <span className="flow-stage__chevron" aria-hidden="true">{open ? '−' : '+'}</span>
                    </span>
                    <span className="flow-stage__tagline">{stage.tagline}</span>
                    <span className="flow-stage__output"><small>AL FINAL</small><strong>{STAGE_OUTPUT[stage.id]}</strong></span>
                    <span className="flow-stage__footer">
                      <span className="flow-stage__where" data-tone={where.tone}>{where.label}</span>
                      {standing && standing.standing !== 'unknown' && (
                        <Badge tone={STANDING_TONE[standing.standing]}>{standing.detail || STANDING_LABEL[standing.standing]}</Badge>
                      )}
                    </span>
                  </button>
                  {open && <StageDetail stage={stage} standing={standing} streamId={streamId} id={detailId} />}
                </li>
              );
            })}
          </ol>
        </li>
      ))}
    </ol>
  );
}

function StageDetail({ stage, standing, streamId, id }: { stage: FlowStage; standing?: { standing: FlowStanding; detail: string }; streamId?: string; id: string }) {
  return (
    <section className="flow-detail" id={id} aria-label={`Detalle de ${stage.label}`}>
      <div className="flow-detail__path">
        <div className="flow-detail__card" data-kind="input">
          <span>RECIBE</span>
          <p>{stage.receives}</p>
        </div>
        <span className="flow-detail__arrow" aria-hidden="true">→</span>
        <div className="flow-detail__card" data-kind="work">
          <span>QUÉ PASA</span>
          <p>{stage.does}</p>
        </div>
        <span className="flow-detail__arrow" aria-hidden="true">→</span>
        <div className="flow-detail__card" data-kind="output">
          <span>QUÉ QUEDA</span>
          <p>{stage.delivers}</p>
        </div>
      </div>
      <div className="flow-detail__notes">
        {stage.cost && (
          <div className="flow-detail__note" data-kind="cost">
            <span aria-hidden="true">$</span><p><strong>Costo</strong>{stage.cost}</p>
          </div>
        )}
        <div className="flow-detail__note" data-kind="failure">
          <span aria-hidden="true">!</span><p><strong>Si algo falla</strong>{stage.failure}</p>
        </div>
      </div>
      {standing && standing.standing !== 'unknown' && (
        <p className="flow-detail__result">
          <span>EN ESTA TRANSMISIÓN</span><strong>{standing.detail}</strong><Badge tone={STANDING_TONE[standing.standing]}>{STANDING_LABEL[standing.standing]}</Badge>
        </p>
      )}

      {stage.to && streamId && (
        <p className="flow-detail__link">
          <Link to={`/stream/${encodeURIComponent(streamId)}/${stage.to}`}>Ver este resultado →</Link>
        </p>
      )}
    </section>
  );
}
